import React, { useState, useEffect, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const API_BASE_URL = 'https://api-vizum-01.lumixoticas.com.br';
const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
const formatDate = (dateString) => new Date(dateString).toLocaleDateString('pt-BR');

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6'];

// Recebemos onOpenOS como propriedade (prop)
export default function FluxoDeCaixa({ onOpenOS }) {
  const [ordens, setOrdens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('resumo'); // resumo, entradas, areceber
  
  // ================= FILTROS =================
  const [filtroLoja, setFiltroLoja] = useState('Todas');
  const [filtroMetodo, setFiltroMetodo] = useState('Todos');
  const [termoBusca, setTermoBusca] = useState('');
  const [dataInicio, setDataInicio] = useState(new Date(new Date().setDate(1)).toISOString().split('T')[0]);
  const [dataFim, setDataFim] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('vizumToken');
        const res = await fetch(`${API_BASE_URL}/ordens-servico`, { 
          headers: { 'Authorization': `Bearer ${token}` } 
        });
        const data = await res.json();
        setOrdens(Array.isArray(data) ? data : []);
      } catch (err) { 
        console.error(err); 
      } finally { 
        setLoading(false); 
      }
    };
    fetchData();
  }, []);

  const stats = useMemo(() => {
    let totalRecebido = 0;
    let aReceberTotal = 0;
    const listaPagamentos = [];
    const listaDevedores = [];
    const metodosDistribuicao = {};

    const buscaLower = termoBusca.toLowerCase().trim();

    ordens.forEach(os => {
      // 1. Filtro de Loja
      if (filtroLoja !== 'Todas' && os.loja !== filtroLoja) return;

      // 2. Filtro de Busca (Cliente ou OS)
      const nomeCliente = (os.cliente?.nome || '').toLowerCase();
      const numeroOS = String(os.numeroOS || '').toLowerCase();
      if (buscaLower && !nomeCliente.includes(buscaLower) && !numeroOS.includes(buscaLower)) {
        return; // Ignora se não bater com a busca
      }

      const dataOSStr = os.createdAt || os.data || os.dataEntrada;
      const dataOS = dataOSStr ? new Date(dataOSStr).toISOString().split('T')[0] : '1970-01-01';

      // ================= CÁLCULO DE A RECEBER =================
      const totalPagoNaOs = (os.pagamentos || []).reduce((acc, pg) => acc + pg.valor, 0);
      const valorTotalOs = os.valorTotal || totalPagoNaOs; 
      const valorPendente = valorTotalOs - totalPagoNaOs;

      // Se a OS foi criada dentro do período filtrado e tem pendência
      if (valorPendente > 0.01 && dataOS >= dataInicio && dataOS <= dataFim) {
        aReceberTotal += valorPendente;
        listaDevedores.push({
          cliente: os.cliente?.nome || 'Cliente não identificado',
          telefone: os.cliente?.telefone || '--',
          os: os.numeroOS,
          osId: os._id, // CAPTURAMOS O ID DA OS PARA PODER ABRIR
          dataOS: dataOSStr,
          valorTotal: valorTotalOs,
          valorPago: totalPagoNaOs,
          valorPendente: valorPendente
        });
      }

      // ================= CÁLCULO DE ENTRADAS (FLUXO DE CAIXA) =================
      (os.pagamentos || []).forEach(pg => {
        const dataPg = new Date(pg.data).toISOString().split('T')[0];
        
        // Filtro de Data do Pagamento
        if (dataPg < dataInicio || dataPg > dataFim) return;

        // Filtro de Método de Pagamento
        const metodoKey = pg.metodo || 'Outros';
        if (filtroMetodo !== 'Todos' && !metodoKey.toLowerCase().includes(filtroMetodo.toLowerCase())) {
          return;
        }

        totalRecebido += pg.valor;
        metodosDistribuicao[metodoKey] = (metodosDistribuicao[metodoKey] || 0) + pg.valor;

        listaPagamentos.push({
          data: pg.data,
          cliente: os.cliente?.nome || 'Desconhecido',
          os: os.numeroOS,
          osId: os._id, // CAPTURAMOS O ID DA OS PARA PODER ABRIR
          metodo: pg.metodo,
          parcelas: pg.parcelas || 1,
          valor: pg.valor
        });
      });
    });

    const grafMetodos = Object.keys(metodosDistribuicao).map(key => ({
      name: key, value: metodosDistribuicao[key]
    }));

    const ticketMedio = listaPagamentos.length > 0 ? (totalRecebido / listaPagamentos.length) : 0;

    return { 
      totalRecebido, 
      aReceberTotal, 
      ticketMedio,
      qtdEntradas: listaPagamentos.length,
      lista: listaPagamentos.sort((a,b) => new Date(b.data) - new Date(a.data)),
      devedores: listaDevedores.sort((a,b) => b.valorPendente - a.valorPendente),
      grafMetodos
    };
  }, [ordens, dataInicio, dataFim, filtroLoja, filtroMetodo, termoBusca]);

  // ==================================================================================
  // TELA DE LOADING ANIMADA (FANTASMINHA DE ÓCULOS - VERSÃO FLUXO DE CAIXA)
  // ==================================================================================
  if (loading) return (
    <div className="fluxo-loader-container">
      <div className="ghost-container">
        {/* SVG do Fantasminha */}
        <svg className="ghost-animated" width="140" height="140" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Corpo do Fantasminha */}
          <path d="M20 50 C20 15, 80 15, 80 50 L80 85 C75 80, 70 90, 65 85 C60 80, 55 90, 50 85 C45 80, 40 90, 35 85 C30 80, 25 90, 20 85 Z" fill="#ffffff" stroke="#cbd5e1" strokeWidth="2"/>
          
          {/* Lentes dos Óculos */}
          <rect x="25" y="42" width="20" height="15" rx="4" fill="#0f172a" />
          <rect x="55" y="42" width="20" height="15" rx="4" fill="#0f172a" />
          
          {/* Armação do Óculos (Ponte e Hastes) */}
          <path d="M45 46 Q50 43 55 46" stroke="#0f172a" strokeWidth="3" fill="none" strokeLinecap="round"/>
          <path d="M18 45 L25 45" stroke="#0f172a" strokeWidth="3" fill="none" strokeLinecap="round"/>
          <path d="M75 45 L82 45" stroke="#0f172a" strokeWidth="3" fill="none" strokeLinecap="round"/>
          
          {/* Reflexo Fashion nas lentes */}
          <path d="M28 45 L40 54" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
          <path d="M58 45 L70 54" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
        </svg>
        {/* Sombra no chão que acompanha o pulo */}
        <div className="ghost-shadow"></div>
      </div>
      
      <h3 className="loading-title">Contando as Moedinhas...</h3>
      <p className="loading-subtitle">Nossos fantasminhas financeiros estão buscando os pagamentos do cofre no além.</p>
      
      <style>{`
        .fluxo-loader-container { height: 100vh; display: flex; flex-direction: column; justify-content: center; align-items: center; background: #f8fafc; text-align: center; padding: 20px; font-family: 'Inter', system-ui, sans-serif; }
        .ghost-container { position: relative; margin-bottom: 24px; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 160px; }
        
        .ghost-animated { animation: floatGhost 2.5s ease-in-out infinite; filter: drop-shadow(0px 10px 15px rgba(0,0,0,0.05)); }
        .ghost-shadow { width: 70px; height: 12px; background: rgba(15, 23, 42, 0.1); border-radius: 50%; margin-top: 20px; animation: scaleShadow 2.5s ease-in-out infinite; }
        
        .loading-title { margin: 0; font-size: 24px; font-weight: 800; color: #0f172a; letter-spacing: -0.5px; }
        .loading-subtitle { margin: 8px 0 0 0; font-size: 15px; color: #64748b; font-weight: 500; max-width: 340px; line-height: 1.5; }

        @keyframes floatGhost {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(3deg); }
        }
        @keyframes scaleShadow {
          0%, 100% { transform: scale(1); opacity: 0.2; }
          50% { transform: scale(0.6); opacity: 0.05; }
        }
      `}</style>
    </div>
  );
  // ==================================================================================

  return (
    <div className="fin-container">
      <header className="fin-header">
        <div className="header-title">
          <h1>Controle de Caixa</h1>
          <p>Acompanhamento de entradas e clientes pendentes.</p>
        </div>
        
        {/* ÁREA DE FILTROS AVANÇADOS */}
        <div className="filters-card">
            <div className="filter-group">
              <label>Buscar (Cliente ou OS)</label>
              <input 
                type="text" 
                placeholder="Ex: João, ou 1045..." 
                value={termoBusca} 
                onChange={e => setTermoBusca(e.target.value)} 
              />
            </div>
            <div className="filter-group">
              <label>Loja</label>
              <select value={filtroLoja} onChange={e => setFiltroLoja(e.target.value)}>
                  <option value="Todas">Todas as Lojas</option>
                  <option value="Vizum 01">Vizum 01</option>
                  <option value="Vizum 02">Vizum 02</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Método de Pagamento</label>
              <select value={filtroMetodo} onChange={e => setFiltroMetodo(e.target.value)}>
                  <option value="Todos">Todos os Métodos</option>
                  <option value="pix">Pix</option>
                  <option value="dinheiro">Dinheiro</option>
                  <option value="crédito">Cartão de Crédito</option>
                  <option value="débito">Cartão de Débito</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Data Inicial</label>
              <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
            </div>
            <div className="filter-group">
              <label>Data Final</label>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
            </div>
        </div>
      </header>

      {/* NAVEGAÇÃO POR ABAS */}
      <div className="tabs-container">
        <button className={activeTab === 'resumo' ? 'active' : ''} onClick={() => setActiveTab('resumo')}>📊 Resumo do Caixa</button>
        <button className={activeTab === 'entradas' ? 'active' : ''} onClick={() => setActiveTab('entradas')}>💰 Histórico de Entradas</button>
        <button className={activeTab === 'areceber' ? 'active' : ''} onClick={() => setActiveTab('areceber')}>⚠️ A Receber (Pendentes)</button>
      </div>

      {/* ABA 1: VISÃO GERAL */}
      {activeTab === 'resumo' && (
        <>
          <div className="kpi-grid">
            <div className="kpi-card card-green">
                <span>Total Recebido (Caixa)</span>
                <h2>{formatCurrency(stats.totalRecebido)}</h2>
                <small>No período selecionado</small>
            </div>
            <div className="kpi-card card-yellow">
                <span>Total a Receber</span>
                <h2>{formatCurrency(stats.aReceberTotal)}</h2>
                <small>De OS geradas neste período</small>
            </div>
            <div className="kpi-card card-blue">
                <span>Ticket Médio</span>
                <h2>{formatCurrency(stats.ticketMedio)}</h2>
                <small>Por transação recebida</small>
            </div>
            <div className="kpi-card card-dark">
                <span>Qtd. de Entradas</span>
                <h2>{stats.qtdEntradas}</h2>
                <small>Pagamentos registrados</small>
            </div>
          </div>

          <div className="charts-section">
            <div className="chart-box">
              <h3>Entradas por Método de Pagamento</h3>
              {stats.grafMetodos.length > 0 ? (
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={stats.grafMetodos} innerRadius={70} outerRadius={110} paddingAngle={5} dataKey="value">
                        {stats.grafMetodos.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="empty-chart">Nenhum dado para exibir no gráfico neste período/filtro.</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ABA 2: ENTRADAS */}
      {activeTab === 'entradas' && (
        <div className="table-card">
          <h3>Histórico de Entradas</h3>
          <p className="text-muted">Lista de todos os pagamentos que entraram no caixa. Clique em uma linha para abrir a O.S.</p>
          <div className="table-responsive">
              <table>
                  <thead>
                      <tr>
                          <th>Data</th>
                          <th>OS</th>
                          <th>Cliente</th>
                          <th>Método de Pagamento</th>
                          <th align="right">Valor Recebido</th>
                      </tr>
                  </thead>
                  <tbody>
                      {stats.lista.map((item, i) => (
                          <tr key={i} className="row-clickable" onClick={() => onOpenOS && onOpenOS(item.osId)}>
                              <td>{formatDate(item.data)}</td>
                              <td className="t-bold text-link">#{item.os}</td>
                              <td>{item.cliente}</td>
                              <td>
                                <span className={`badge-metodo ${item.metodo?.toLowerCase()}`}>
                                  {item.metodo} {item.parcelas > 1 ? `(${item.parcelas}x)` : ''}
                                </span>
                              </td>
                              <td align="right" className="t-green t-bold">{formatCurrency(item.valor)}</td>
                          </tr>
                      ))}
                      {stats.lista.length === 0 && <tr><td colSpan="5" className="t-center">Nenhuma entrada encontrada para os filtros atuais.</td></tr>}
                  </tbody>
              </table>
          </div>
        </div>
      )}

      {/* ABA 3: A RECEBER (INADIMPLENTES) */}
      {activeTab === 'areceber' && (
        <div className="table-card border-yellow">
          <h3>Contas a Receber (OS Pendentes)</h3>
          <p className="text-muted">Clientes que possuem saldo devedor. Clique na linha para abrir a OS.</p>
          <div className="table-responsive">
              <table>
                  <thead>
                      <tr>
                          <th>Cliente</th>
                          <th>Contato</th>
                          <th>Nº OS</th>
                          <th>Data da OS</th>
                          <th>Valor Total</th>
                          <th>Valor Pago</th>
                          <th align="right">Falta Pagar</th>
                      </tr>
                  </thead>
                  <tbody>
                      {stats.devedores.map((item, i) => (
                          <tr key={i} className="row-clickable" onClick={() => onOpenOS && onOpenOS(item.osId)}>
                              <td className="t-bold">{item.cliente}</td>
                              <td>{item.telefone}</td>
                              <td className="t-bold text-link">#{item.os}</td>
                              <td>{formatDate(item.dataOS)}</td>
                              <td>{formatCurrency(item.valorTotal)}</td>
                              <td className="t-green">{formatCurrency(item.valorPago)}</td>
                              <td align="right" className="t-red t-bold">{formatCurrency(item.valorPendente)}</td>
                          </tr>
                      ))}
                      {stats.devedores.length === 0 && <tr><td colSpan="7" className="t-center">Nenhum cliente devendo neste período! 🎉</td></tr>}
                  </tbody>
              </table>
          </div>
        </div>
      )}

      <style>{`
        .fin-container { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', system-ui, sans-serif; }
        .fin-header { margin-bottom: 20px; }
        .header-title h1 { margin: 0 0 5px 0; font-size: 28px; color: #0f172a; }
        .header-title p { margin: 0 0 20px 0; color: #64748b; font-size: 15px; }
        
        /* Filtros */
        .filters-card { background: #fff; padding: 20px; border-radius: 12px; border: 1px solid #e2e8f0; display: flex; flex-wrap: wrap; gap: 15px; align-items: flex-end; }
        .filter-group { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 150px; }
        .filter-group label { font-size: 12px; font-weight: 600; color: #475569; text-transform: uppercase; }
        .filter-group input, .filter-group select { padding: 10px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px; outline: none; transition: 0.2s; }
        .filter-group input:focus, .filter-group select:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }

        /* Tabs */
        .tabs-container { display: flex; gap: 10px; margin-bottom: 25px; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px; overflow-x: auto; }
        .tabs-container button { padding: 10px 20px; border: none; background: transparent; font-size: 15px; font-weight: 600; color: #64748b; cursor: pointer; border-radius: 8px; transition: 0.2s; white-space: nowrap; }
        .tabs-container button:hover { background: #f1f5f9; color: #0f172a; }
        .tabs-container button.active { background: #0f172a; color: #fff; }

        /* KPIs */
        .kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 20px; margin-bottom: 30px; }
        .kpi-card { padding: 25px; border-radius: 16px; color: #fff; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); display: flex; flex-direction: column; justify-content: center; }
        .kpi-card span { font-size: 13px; font-weight: 700; text-transform: uppercase; opacity: 0.9; }
        .kpi-card h2 { margin: 10px 0 5px 0; font-size: 32px; font-weight: 800; }
        .kpi-card small { font-size: 13px; opacity: 0.9; font-weight: 500; }
        
        .card-green { background: linear-gradient(135deg, #10b981, #059669); }
        .card-yellow { background: linear-gradient(135deg, #f59e0b, #d97706); }
        .card-blue { background: linear-gradient(135deg, #3b82f6, #2563eb); }
        .card-dark { background: linear-gradient(135deg, #334155, #0f172a); }

        /* Charts Section */
        .charts-section { display: grid; grid-template-columns: 1fr; margin-bottom: 30px; }
        .chart-box { background: #fff; padding: 25px; border-radius: 16px; border: 1px solid #e2e8f0; max-width: 600px; margin: 0 auto; width: 100%; }
        .chart-box h3 { margin: 0 0 20px 0; font-size: 16px; color: #334155; text-align: center; }
        .empty-chart { text-align: center; color: #94a3b8; padding: 50px 0; font-style: italic; }
        
        /* Tables */
        .table-card { background: #fff; padding: 25px; border-radius: 16px; border: 1px solid #e2e8f0; }
        .table-card.border-yellow { border-top: 4px solid #f59e0b; }
        .table-card h3 { margin: 0 0 5px 0; color: #1e293b; }
        .text-muted { color: #64748b; font-size: 14px; margin-bottom: 20px; }
        
        .table-responsive { overflow-x: auto; max-height: 600px; overflow-y: auto; }
        table { width: 100%; border-collapse: collapse; min-width: 700px; }
        th { text-align: left; padding: 15px; font-size: 12px; text-transform: uppercase; color: #64748b; background: #f8fafc; position: sticky; top: 0; z-index: 1; }
        td { padding: 15px; font-size: 14px; border-bottom: 1px solid #f1f5f9; color: #334155; transition: 0.2s; }
        
        /* Linhas clicáveis (NOVO) */
        .row-clickable { cursor: pointer; }
        .row-clickable:hover td { background: #f1f5f9; }
        .text-link { color: #3b82f6 !important; text-decoration: underline; text-underline-offset: 3px; }

        /* Utils */
        .t-bold { font-weight: 700; color: #0f172a; }
        .t-center { text-align: center; }
        .t-green { color: #059669; }
        .t-red { color: #dc2626; }
        
        /* Badges */
        .badge-metodo { padding: 5px 10px; border-radius: 6px; font-size: 12px; font-weight: 700; text-transform: uppercase; display: inline-block; }
        .badge-metodo.pix { background: #dcfce7; color: #166534; }
        .badge-metodo.dinheiro { background: #fef3c7; color: #92400e; }
        .badge-metodo.crédito, .badge-metodo.credito { background: #dbeafe; color: #1e40af; }
        .badge-metodo.débito, .badge-metodo.debito { background: #f3e8ff; color: #6b21a8; }

        @media (max-width: 768px) {
          .filters-card { flex-direction: column; align-items: stretch; }
          .kpi-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}
